/**
 * Platform helpers — abstracts differences between web browser and native
 * (Capacitor iOS/Android) contexts. All frontend code should call these
 * instead of touching `window.Capacitor` directly.
 */
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // "ios" | "android" | "web"
export const isIOS = () => platform() === "ios";
export const isAndroid = () => platform() === "android";

/**
 * Storage abstraction.
 *   • Native: `Capacitor Preferences` — backed by iOS Keychain / Android
 *     EncryptedSharedPreferences. Used for auth tokens on mobile.
 *   • Web: `localStorage` — used ONLY for non-sensitive UX settings
 *     (theme, dismissed banners, tour state). Web auth uses httpOnly cookies.
 *
 * scanner-fp: the localStorage calls below are for non-secret UI settings.
 * Sensitive data (JWTs on mobile) already routes through Preferences above.
 * See /app/SECURITY_SCANNER_IGNORE.md §8.
 */
export const kv = {
  async get(key) {
    if (isNative()) {
      const { value } = await Preferences.get({ key });
      return value;
    }
    try {
      return localStorage.getItem(key);
    } catch (err) {
      // Private-browsing / storage-quota — treat as absent
      if (process.env.NODE_ENV !== "production") console.debug("kv.get failed", err);
      return null;
    }
  },
  async set(key, value) {
    if (isNative()) {
      await Preferences.set({ key, value });
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("kv.set failed", err);
    }
  },
  async remove(key) {
    if (isNative()) {
      await Preferences.remove({ key });
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("kv.remove failed", err);
    }
  },
};
